// src/pages/dataInput/utils/sheetValidation.ts
import * as XLSX from "xlsx";
import { extractIndexEntries, looksLikeIndexSheet, IndexEntry } from "./indexSheetParser";

// ── Alias sets — keep in sync with excel_dm_synthesizer.py ───────────────────

const FIELD_NAME_ALIASES = new Set([
  "field_name", "fieldname", "field name", "name", "column_name",
  "columnname", "column name", "attribute_name", "attribute name",
  "metric name", "metricname",
  "field", "column", "property name", "property_name",
  "kpi name", "kpiname",
]);

const DESCRIPTION_ALIASES = new Set([
  "description", "desc", "short_desc", "short desc", "field_description",
  "field description", "column_description", "column description",
  "business_description", "business description",
  "metric description",
]);

const SOURCE_TYPE_ALIASES = new Set([
  "source_data_type", "source data type", "sourcedatatype",
  "data_type", "data type", "datatype",
  "source_type", "source type", "sourcetype",
  "type", "field_type", "field type",
]);

const TABLE_NAME_ALIASES = new Set([
  "table_name", "tablename", "table name", "table",
  "source_table", "source table",
  "tracker",
  "entity/table name", "entity / table name",
  "entity table name", "entity_table_name",
  "object name", "object_name",
  "entity name", "entity_name",
]);

const ATTRIBUTES_ALIASES = new Set([
  "attributes", "attribute", "attrs", "attr",
]);

const ALL_KNOWN_ALIASES = new Set<string>([
  ...FIELD_NAME_ALIASES, ...DESCRIPTION_ALIASES,
  ...SOURCE_TYPE_ALIASES, ...TABLE_NAME_ALIASES, ...ATTRIBUTES_ALIASES,
  // extras the server knows about — prevents false NEAR_MISS hits
  "key", "is_key", "dtel", "data element", "value help", "value_help",
  "transaction bp", "transaction", "cmp", "gov", "mop",
  "entity type", "entity_type", "comments", "comment", "notes",
  "segment", "element", "len", "length", "field length",
  "relates to", "relates_to", "reference", "ref",
  "business element name",
]);

// ── Types ─────────────────────────────────────────────────────────────────────

export type SheetMode = "enriched" | "typed" | "raw" | "empty";

/** One inline data-quality warning on a sheet */
export interface SheetWarning {
  id:       string;               // e.g. "BLANK_FIELD", "DUPLICATE", "SPECIAL_CHAR"
  severity: "error" | "warning" | "info";
  message:  string;
  samples?: string[];             // first few examples
}

export interface SheetClassification {
  sheetName:          string;
  mode:               SheetMode;
  rowCount:           number;
  colCount:           number;
  headers:            string[];
  detectedHeaderRow:  number;
  detectedColumns: {
    field?:       string;
    description?: string;
    type?:        string;
    table?:       string;
  };
  reason:   string;
  /** Inline data-quality warnings — populated when a field column is detected */
  warnings: SheetWarning[];
  /** How many data rows will actually be processed (total - blank - invalid) */
  processableRows: number;
  /** Unique field count after deduplication (processable - duplicate extras) */
  uniqueFields: number;
}

export interface FileClassification {
  fileName:     string;
  fileType:     "csv" | "excel" | "ddl" | "other";
  sheets:       SheetClassification[];
  indexEntries: Map<string, IndexEntry> | null;
}

export type Tool = "BUILD_DG_USING_DATA" | "BUILD_DQ_USING_DATA" | "BUILD_MDM_USING_DATA";

const TOOL_ACCEPTED_MODES: Record<Tool, Set<SheetMode>> = {
  BUILD_DG_USING_DATA:  new Set(["enriched", "typed", "raw"]),
  BUILD_DQ_USING_DATA:  new Set(["raw"]),
  BUILD_MDM_USING_DATA: new Set(["raw"]),
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const normalise = (s: any) =>
  String(s ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const headersMatchAliases = (headers: string[]): boolean =>
  headers.some(h => ALL_KNOWN_ALIASES.has(normalise(h)));

const valuesMatchAliases = (values: any[]): boolean =>
  values.some(v => ALL_KNOWN_ALIASES.has(normalise(v)));

const findColIn = (headers: string[], aliases: Set<string>): string | undefined =>
  headers.find(h => aliases.has(normalise(h)));

const INVALID_NAMES = new Set(["", "--", "n/a", "na", "-", "none", "null", "nan"]);
const SPECIAL_CHAR_RE = /[^\w\s\-\.]/;
// Prefixes that indicate comments/formulas/annotations — not real field names
const SKIP_PREFIXES = ["==", "?", "//", "/*", "#!", ">>>", "---", "***", "NOTE:", "N.B."];

function detectHeaderRow(ws: XLSX.WorkSheet): {
  headers: string[];
  dataPreview: any[][];
  allDataRows: any[][];
  headerRowIdx: number;
  totalRows: number;
} {
  const aoa: any[][] = XLSX.utils.sheet_to_json(ws, {
    header: 1, defval: "", blankrows: false, raw: false,
  }) as any[][];

  if (aoa.length === 0) {
    return { headers: [], dataPreview: [], allDataRows: [], headerRowIdx: 0, totalRows: 0 };
  }

  const row0 = aoa[0].map(c => String(c ?? "").trim());
  if (headersMatchAliases(row0)) {
    const dataRows = aoa.slice(1);
    return {
      headers: row0, dataPreview: dataRows.slice(0, 5),
      allDataRows: dataRows,
      headerRowIdx: 0, totalRows: dataRows.length,
    };
  }

  for (let r = 1; r < Math.min(5, aoa.length); r++) {
    if (valuesMatchAliases(aoa[r].map(c => String(c ?? "").trim()))) {
      const headers = aoa[r].map(c => String(c ?? "").trim());
      const dataRows = aoa.slice(r + 1);
      return {
        headers,
        dataPreview: dataRows.slice(0, 5),
        allDataRows: dataRows,
        headerRowIdx: r, totalRows: dataRows.length,
      };
    }
  }

  const dataRows = aoa.slice(1);
  return {
    headers: row0, dataPreview: dataRows.slice(0, 5),
    allDataRows: dataRows,
    headerRowIdx: 0, totalRows: dataRows.length,
  };
}

function hasMeaningfulDescriptions(
  headers: string[], preview: any[][], descCol?: string
): boolean {
  if (!descCol) return false;
  const idx = headers.indexOf(descCol);
  if (idx < 0 || preview.length === 0) return false;
  let nonEmpty = 0;
  for (const row of preview) {
    if (String(row[idx] ?? "").trim()) nonEmpty++;
  }
  return nonEmpty / preview.length >= 0.3;
}

// ── Data quality scan (runs on ALL data rows, not just preview) ──────────────

function scanDataQuality(
  headers: string[],
  allDataRows: any[][],
  fieldCol?: string,
  tableCol?: string,
): { warnings: SheetWarning[]; processableRows: number; uniqueFields: number } {
  const warnings: SheetWarning[] = [];
  if (!fieldCol) return { warnings, processableRows: allDataRows.length, uniqueFields: allDataRows.length };

  const fieldIdx = headers.indexOf(fieldCol);
  const tableIdx = tableCol ? headers.indexOf(tableCol) : -1;
  if (fieldIdx < 0) return { warnings, processableRows: allDataRows.length, uniqueFields: allDataRows.length };

  let blankCount = 0;
  const blankSamples: string[] = [];
  let commentPrefixCount = 0;
  const commentSamples: string[] = [];
  const seen = new Map<string, number[]>();     // "table::field" → row numbers
  let specialCharCount = 0;
  const specialSamples: string[] = [];
  let processable = 0;

  for (let i = 0; i < allDataRows.length; i++) {
    const row     = allDataRows[i];
    const rawField = String(row[fieldIdx] ?? "").trim();
    const rawTable = tableIdx >= 0 ? String(row[tableIdx] ?? "").trim() : "";
    const rowNum   = i + 2;   // 1-based + header row

    // Blank / invalid field name (truly empty or sentinel values)
    const isBlank = INVALID_NAMES.has(normalise(rawField))
      || !/[a-zA-Z0-9]/.test(rawField);
    if (isBlank) {
      blankCount++;
      if (blankSamples.length < 5) blankSamples.push(`row ${rowNum}`);
      continue;
    }

    // Comment/formula prefix — synthesizer drops these (prefix stripped → collision/empty)
    if (SKIP_PREFIXES.some(p => rawField.startsWith(p))) {
      commentPrefixCount++;
      if (commentSamples.length < 5) {
        commentSamples.push(`"${rawField.slice(0, 45)}${rawField.length > 45 ? '…' : ''}"`);
      }
      continue;  // NOT counted as processable — matches synthesizer behavior
    }

    processable++;

    // Duplicate check (table + field combo)
    const key = `${normalise(rawTable)}::${normalise(rawField)}`;
    const prev = seen.get(key);
    if (prev) {
      prev.push(rowNum);
    } else {
      seen.set(key, [rowNum]);
    }

    // Special characters in field name
    if (SPECIAL_CHAR_RE.test(rawField)) {
      specialCharCount++;
      if (specialSamples.length < 5) {
        const chars = rawField.match(/[^\w\s\-\.]/g) || [];
        specialSamples.push(`"${rawField}" (${[...new Set(chars)].join("")})`);
      }
    }

    // Special characters in table name
    if (rawTable && SPECIAL_CHAR_RE.test(rawTable) && specialSamples.length < 5) {
      // Only flag if not already counted
    }
  }

  // Blank field names
  if (blankCount > 0) {
    warnings.push({
      id:       "BLANK_FIELD",
      severity: "warning",
      message:  `${blankCount} row(s) have blank/invalid field names — will be dropped`,
      samples:  blankSamples,
    });
  }

  // Comment/formula prefix fields — processed but flagged
  if (commentPrefixCount > 0) {
    warnings.push({
      id:       "COMMENT_PREFIX",
      severity: "warning",
      message:  `${commentPrefixCount} field name(s) start with ==, ?, // etc. — will be dropped`,
      samples:  commentSamples,
    });
  }

  // Duplicates (computed at function scope so uniqueFields can use it)
  const dupes = [...seen.entries()].filter(([, rows]) => rows.length > 1);
  const totalDupExtras = dupes.reduce((s, [, r]) => s + r.length - 1, 0);

  if (dupes.length > 0) {
    const dupSamples = dupes.slice(0, 4).map(([key, rows]) => {
      const parts = key.split("::");
      const label = parts[0] ? `${parts[0]}.${parts[1]}` : parts[1];
      return `"${label}" × ${rows.length}`;
    });
    warnings.push({
      id:       "DUPLICATE",
      severity: "warning",
      message:  `${dupes.length} field(s) appear multiple times (${totalDupExtras} extra rows) — duplicates get a suffix`,
      samples:  dupSamples,
    });
  }

  // Special characters
  if (specialCharCount > 0) {
    warnings.push({
      id:       "SPECIAL_CHAR",
      severity: "info",
      message:  `${specialCharCount} field name(s) have special characters — will be stripped`,
      samples:  specialSamples,
    });
  }

  return { warnings, processableRows: processable, uniqueFields: seen.size };
}

// ── Classify one sheet ───────────────────────────────────────────────────────

function classifySheet(sheetName: string, ws: XLSX.WorkSheet): SheetClassification {
  const { headers, dataPreview, allDataRows, headerRowIdx, totalRows } = detectHeaderRow(ws);

  if (headers.length === 0 || headers.every(h => !h)) {
    return {
      sheetName, mode: "empty", rowCount: 0, colCount: 0,
      headers: [], detectedHeaderRow: 0, detectedColumns: {},
      reason: "Sheet is empty or has no header row.",
      warnings: [], processableRows: 0, uniqueFields: 0,
    };
  }

  const fieldCol = findColIn(headers, FIELD_NAME_ALIASES);
  const descCol  = findColIn(headers, DESCRIPTION_ALIASES);
  const typeCol  = findColIn(headers, SOURCE_TYPE_ALIASES);
  const tableCol = findColIn(headers, TABLE_NAME_ALIASES);
  const detectedColumns = { field: fieldCol, description: descCol, type: typeCol, table: tableCol };
  const colCount = headers.length;

  // Run data quality scan on ALL rows
  const { warnings, processableRows, uniqueFields } = scanDataQuality(headers, allDataRows, fieldCol, tableCol);

  if (!fieldCol) {
    return {
      sheetName, mode: "raw", rowCount: totalRows, colCount, headers,
      detectedHeaderRow: headerRowIdx, detectedColumns,
      reason: `Headers in row ${headerRowIdx + 1} look like data column names. Treated as raw data.`,
      warnings, processableRows: totalRows, uniqueFields: totalRows,
    };
  }

  const hasDesc = hasMeaningfulDescriptions(headers, dataPreview, descCol);
  const hasType = !!typeCol;

  const base = {
    sheetName, rowCount: totalRows, colCount, headers,
    detectedHeaderRow: headerRowIdx, detectedColumns,
    warnings, processableRows, uniqueFields,
  };

  if (hasDesc) {
    return {
      ...base, mode: "enriched",
      reason: `Detected enriched metadata: '${tableCol||``}' + '${fieldCol}' + descriptions` + (typeCol ? ` + types` : ``) + `.`,
    };
  }
  if (hasType) {
    return {
      ...base, mode: "typed",
      reason: `Detected typed metadata: '${tableCol||``}' + '${fieldCol}' + '${typeCol}', but no descriptions.`,
    };
  }

  return {
    ...base, mode: "typed",
    reason: `Detected '${tableCol||``}' + '${fieldCol}' column but no descriptions or types. LLM will infer.`,
  };
}

// ── Public: classify a single file ───────────────────────────────────────────

export async function classifyFile(file: File): Promise<FileClassification> {
  const lower = file.name.toLowerCase();

  if (/\.(sql|ddl)$/i.test(lower)) {
    return { fileName: file.name, fileType: "ddl", sheets: [], indexEntries: null };
  }

  if (/\.csv$/i.test(lower)) {
    const text = await file.text();
    const wb   = XLSX.read(text, { type: "string" });
    const wsName = wb.SheetNames[0] || "Sheet1";
    // Use the CSV filename (without extension) as the display name, not "Sheet1"
    const displayName = file.name.replace(/\.[^.]+$/, "");
    const sc = classifySheet(displayName, wb.Sheets[wsName]);
    return { fileName: file.name, fileType: "csv", sheets: [sc], indexEntries: null };
  }

  if (/\.(xls|xlsx|xlsm)$/i.test(lower)) {
    const buf = await file.arrayBuffer();

    let wb: XLSX.WorkBook;
    try {
      wb = XLSX.read(buf, { type: "array" });
    } catch (xlsxErr: any) {
      try {
        const text = await file.text();
        wb = XLSX.read(text, { type: "string" });
      } catch {
        return {
          fileName: file.name,
          fileType: "excel" as const,
          sheets: [{
            sheetName: "Sheet1", mode: "empty" as const,
            rowCount: 0, colCount: 0, headers: [],
            detectedHeaderRow: 0, detectedColumns: {},
            reason: `Could not read file: ${xlsxErr?.message ?? "unknown error"}.`,
            warnings: [], processableRows: 0, uniqueFields: 0,
          }],
          indexEntries: null,
        };
      }
    }

    const indexEntries = extractIndexEntries(wb);
    const dataSheetNames = wb.SheetNames.filter(n => !looksLikeIndexSheet(n));
    const sheets = dataSheetNames.map(n => classifySheet(n, wb.Sheets[n]));

    return { fileName: file.name, fileType: "excel", sheets, indexEntries };
  }

  return { fileName: file.name, fileType: "other", sheets: [], indexEntries: null };
}

// ── DG template check ────────────────────────────────────────────────────────

const DG_REQUIRED: { label: string; key: keyof SheetClassification["detectedColumns"] }[] = [
  { label: "Field/Column Name", key: "field" },
  { label: "Description",       key: "description" },
  { label: "Source/Data Type",   key: "type" },
  { label: "Entity/Table Name",  key: "table" },
];

export function checkDGTemplate(sheet: SheetClassification): { ok: boolean; missing: string[] } {
  const missing = DG_REQUIRED
    .filter(c => !sheet.detectedColumns[c.key])
    .map(c => c.label);
  return { ok: missing.length === 0, missing };
}

function dgTemplateMessage(missing: string[]): string {
  return (
    `File does not follow the required template for DG import. ` +
    `Missing required column(s): ${missing.join(", ")}. ` +
    `A DG sheet must include a field/column name, a description, a ` +
    `source/data type, and an entity/table name.`
  );
}

// ── Public: tool-aware acceptance check ──────────────────────────────────────

export interface SheetAcceptance {
  accepted: boolean;
  reason:   string;
}

export function checkSheetAcceptance(
  sheet: SheetClassification, tool: Tool
): SheetAcceptance {
  if (sheet.mode === "empty") return { accepted: false, reason: sheet.reason };

  if (tool === "BUILD_DG_USING_DATA") {
    const tmpl = checkDGTemplate(sheet);
    return tmpl.ok
      ? { accepted: true,  reason: sheet.reason }
      : { accepted: false, reason: dgTemplateMessage(tmpl.missing) };
  }

  const accepted = TOOL_ACCEPTED_MODES[tool].has(sheet.mode);
  if (accepted) return { accepted: true, reason: sheet.reason };

  if (sheet.mode === "enriched" || sheet.mode === "typed") {
    return {
      accepted: false,
      reason:
        `This sheet contains metadata ('${sheet.detectedColumns.field}'` +
        (sheet.detectedColumns.description ? ` + descriptions` : ``) +
        (sheet.detectedColumns.type ? ` + types` : ``) +
        `) instead of raw data. ` +
        `${tool === "BUILD_DQ_USING_DATA" ? "DQ" : "MDM"} requires raw data.`,
    };
  }

  return { accepted: false, reason: sheet.reason };
}

export function checkFileAcceptance(
  file: FileClassification, tool: Tool
): { anyAccepted: boolean; perSheet: SheetAcceptance[] } {
  if (file.fileType === "ddl") {
    return { anyAccepted: true, perSheet: [{ accepted: true, reason: "DDL file." }] };
  }
  if (file.fileType === "other") {
    return {
      anyAccepted: false,
      perSheet: [{ accepted: false, reason: "Unsupported file type." }],
    };
  }
  const perSheet = file.sheets.map(s => checkSheetAcceptance(s, tool));
  return { anyAccepted: perSheet.some(p => p.accepted), perSheet };
}

// ── Public: SheetJS-based row/header sample ──────────────────────────────────

export async function readSheetSample(
  file: File, sheetName: string | null, maxRows = 200
): Promise<{ headers: string[]; rows: any[][] }> {
  const buf = file.name.toLowerCase().endsWith(".csv")
    ? await file.text()
    : await file.arrayBuffer();
  const wb = XLSX.read(buf as any, {
    type: typeof buf === "string" ? "string" : "array",
    sheetRows: maxRows + 5,
  });
  const target = sheetName || wb.SheetNames[0];
  const ws     = wb.Sheets[target];
  if (!ws) return { headers: [], rows: [] };
  const aoa: any[][] = XLSX.utils.sheet_to_json(ws, {
    header: 1, defval: "", blankrows: false, raw: false,
  }) as any[][];
  if (aoa.length === 0) return { headers: [], rows: [] };
  return {
    headers: aoa[0].map(h => String(h ?? "").trim()),
    rows:    aoa.slice(1, maxRows + 1),
  };
}

// ── Validation report Excel download ─────────────────────────────────────────

/**
 * Generate an Excel workbook with one sheet per validation issue type.
 * Re-reads the file to extract the actual problematic rows (not just counts).
 * Call from a "Download report" button in FileSource.
 */
export async function generateValidationReport(
  file: File,
  sheetName: string,
): Promise<void> {
  const buf = file.name.toLowerCase().endsWith(".csv")
    ? await file.text()
    : await file.arrayBuffer();
  const wb = XLSX.read(buf as any, {
    type: typeof buf === "string" ? "string" : "array",
  });

  const ws = wb.Sheets[sheetName];
  if (!ws) return;

  const { headers, allDataRows, headerRowIdx } = detectHeaderRow(ws);
  const fieldCol = findColIn(headers, FIELD_NAME_ALIASES);
  const tableCol = findColIn(headers, TABLE_NAME_ALIASES);
  const descCol  = findColIn(headers, DESCRIPTION_ALIASES);
  const typeCol  = findColIn(headers, SOURCE_TYPE_ALIASES);

  if (!fieldCol) return;

  const fieldIdx = headers.indexOf(fieldCol);
  const tableIdx = tableCol ? headers.indexOf(tableCol) : -1;
  const descIdx  = descCol  ? headers.indexOf(descCol)  : -1;
  const typeIdx  = typeCol  ? headers.indexOf(typeCol)   : -1;
  if (fieldIdx < 0) return;

  // ── Collect rows per issue ─────────────────────────────────────────────
  const blankRows:    any[][] = [];
  const duplicateRows: any[][] = [];
  const specialRows:  any[][] = [];
  const commentRows:  any[][] = [];

  const seen = new Map<string, { firstRow: number; rows: any[][] }>();
  const reportHeaders = [
    "Row #", "Field Name", "Table Name", "Description", "Data Type", "Issue", "Severity", "Action Required"
  ];

  for (let i = 0; i < allDataRows.length; i++) {
    const row      = allDataRows[i];
    const rawField = String(row[fieldIdx] ?? "").trim();
    const rawTable = tableIdx >= 0 ? String(row[tableIdx] ?? "").trim() : "";
    const rawDesc  = descIdx  >= 0 ? String(row[descIdx]  ?? "").trim() : "";
    const rawType  = typeIdx  >= 0 ? String(row[typeIdx]  ?? "").trim() : "";
    const rowNum   = i + headerRowIdx + 2;

    const baseRow = [rowNum, rawField, rawTable, rawDesc, rawType];

    const isBlank = INVALID_NAMES.has(normalise(rawField)) || !/[a-zA-Z0-9]/.test(rawField);
    if (isBlank) {
      blankRows.push([...baseRow, rawField ? `Invalid value: "${rawField}"` : "Empty", "Warning", "Row will be skipped — fix field name or remove row"]);
      continue;
    }

    if (SKIP_PREFIXES.some(p => rawField.startsWith(p))) {
      const prefix = SKIP_PREFIXES.find(p => rawField.startsWith(p)) || "";
      commentRows.push([...baseRow, `Starts with "${prefix}"`, "Warning", "Row will be dropped — field name starts with a comment/formula prefix"]);
      continue;
    }

    if (SPECIAL_CHAR_RE.test(rawField)) {
      const chars = rawField.match(/[^\w\s\-\.]/g) || [];
      specialRows.push([...baseRow, `Contains: ${[...new Set(chars)].join(" ")}`, "Info", "No action — characters stripped automatically in PascalCase conversion"]);
    }

    const key = `${normalise(rawTable)}::${normalise(rawField)}`;
    const prev = seen.get(key);
    if (prev) {
      prev.rows.push(baseRow);
    } else {
      seen.set(key, { firstRow: rowNum, rows: [baseRow] });
    }
  }

  for (const [key, { rows }] of seen) {
    if (rows.length > 1) {
      for (const r of rows) {
        duplicateRows.push([...r, `Appears ${rows.length}× (${key.replace("::", ".")})`, "Warning", "Review — duplicate gets numeric suffix (e.g. Name2). Consider deduplicating in source."]);
      }
    }
  }

  const uniqueFields = seen.size;
  // Processable = total - blank - comment prefix (both are dropped by synthesizer)
  const processable  = allDataRows.length - blankRows.length - commentRows.length;

  const outWb = XLSX.utils.book_new();

  const summaryData = [
    ["Validation Report", ""],
    ["File", file.name],
    ["Sheet", sheetName],
    [""],
    ["Metric", "Count", "Severity", "Notes"],
    ["Total Rows (excl. header)", allDataRows.length, "", ""],
    ["Blank/Invalid (dropped)", blankRows.length, "Warning", "Rows with empty or invalid field names"],
    ["Comment/Formula Prefix (dropped)", commentRows.length, "Warning", "Field names starting with ==, ?, // — treated as annotations, not fields"],
    ["★ Fields Will Be Loaded", processable, "", "★ Final field count loaded into your data model"],
    ["Unique Field Names", uniqueFields, "", "Distinct (table + field) combos — duplicates get numeric suffix (e.g. Name2)"],
    ["Duplicate Extras", duplicateRows.length, "Warning", "Extra occurrences beyond first — renamed with suffix, not dropped"],
    ["Special Characters", specialRows.length, "Info", "No action — characters stripped automatically in PascalCase conversion"],
    [""],
    ["Header Row", headerRowIdx + 1, "", ""],
    ["Detected Field Column", fieldCol || "(none)", "", ""],
    ["Detected Table Column", tableCol || "(none)", "", ""],
  ];
  const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
  summaryWs["!cols"] = [{ wch: 35 }, { wch: 12 }, { wch: 10 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(outWb, summaryWs, "Summary");

  const addSheet = (name: string, rows: any[][]) => {
    if (rows.length === 0) {
      const emptyWs = XLSX.utils.aoa_to_sheet([["No issues found"]]);
      XLSX.utils.book_append_sheet(outWb, emptyWs, name);
    } else {
      const data = [reportHeaders, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws["!cols"] = [
        { wch: 8 },   // Row #
        { wch: 45 },  // Field Name
        { wch: 25 },  // Table Name
        { wch: 40 },  // Description
        { wch: 15 },  // Data Type
        { wch: 50 },  // Issue
        { wch: 10 },  // Severity
        { wch: 55 },  // Action Required
      ];
      XLSX.utils.book_append_sheet(outWb, ws, name);
    }
  };

  addSheet("Blank Fields", blankRows);
  addSheet("Duplicates", duplicateRows);
  addSheet("Comment Prefix", commentRows);
  addSheet("Special Chars", specialRows);

  // ── Trigger download ───────────────────────────────────────────────────
  const outBuf = XLSX.write(outWb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([outBuf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${file.name.replace(/\.[^.]+$/, "")}_${sheetName}_validation.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generate a combined validation report for ALL sheets in a file.
 * One summary sheet + per-issue-type sheets aggregated across all sheets.
 */
export async function generateCombinedValidationReport(
  file: File,
  sheetNames: string[],
): Promise<void> {
  const buf = file.name.toLowerCase().endsWith(".csv")
    ? await file.text()
    : await file.arrayBuffer();
  const wb = XLSX.read(buf as any, {
    type: typeof buf === "string" ? "string" : "array",
  });

  const reportHeaders = ["Sheet", "Row #", "Field Name", "Table Name", "Description", "Data Type", "Issue", "Severity", "Action Required"];
  const allBlank:    any[][] = [];
  const allDuplicate: any[][] = [];
  const allSpecial:  any[][] = [];
  const allComment:  any[][] = [];
  const sheetSummaries: any[][] = [];

  for (const sheetName of sheetNames) {
    const wsName = wb.SheetNames.includes(sheetName) ? sheetName : wb.SheetNames[0];
    const ws = wb.Sheets[wsName];
    if (!ws) continue;

    const { headers, allDataRows, headerRowIdx } = detectHeaderRow(ws);
    const fieldCol = findColIn(headers, FIELD_NAME_ALIASES);
    const tableCol = findColIn(headers, TABLE_NAME_ALIASES);
    const descCol  = findColIn(headers, DESCRIPTION_ALIASES);
    const typeCol  = findColIn(headers, SOURCE_TYPE_ALIASES);
    if (!fieldCol) continue;

    const fieldIdx = headers.indexOf(fieldCol);
    const tableIdx = tableCol ? headers.indexOf(tableCol) : -1;
    const descIdx  = descCol  ? headers.indexOf(descCol)  : -1;
    const typeIdx  = typeCol  ? headers.indexOf(typeCol)   : -1;
    if (fieldIdx < 0) continue;

    let blankCount = 0, commentCount = 0, specialCount = 0;
    const seen = new Map<string, { rows: any[][] }>();

    for (let i = 0; i < allDataRows.length; i++) {
      const row      = allDataRows[i];
      const rawField = String(row[fieldIdx] ?? "").trim();
      const rawTable = tableIdx >= 0 ? String(row[tableIdx] ?? "").trim() : "";
      const rawDesc  = descIdx  >= 0 ? String(row[descIdx]  ?? "").trim() : "";
      const rawType  = typeIdx  >= 0 ? String(row[typeIdx]  ?? "").trim() : "";
      const rowNum   = i + headerRowIdx + 2;
      const base = [sheetName, rowNum, rawField, rawTable, rawDesc, rawType];

      const isBlank = INVALID_NAMES.has(normalise(rawField)) || !/[a-zA-Z0-9]/.test(rawField);
      if (isBlank) {
        blankCount++;
        allBlank.push([...base, rawField ? `Invalid: "${rawField}"` : "Empty", "Warning", "Row will be skipped — fix field name or remove row"]);
        continue;
      }

      if (SKIP_PREFIXES.some(p => rawField.startsWith(p))) {
        commentCount++;
        const pfx = SKIP_PREFIXES.find(p => rawField.startsWith(p)) || "";
        allComment.push([...base, `Starts with "${pfx}"`, "Warning", "Row will be dropped — comment/formula prefix"]);
        continue;
      }

      if (SPECIAL_CHAR_RE.test(rawField)) {
        specialCount++;
        const chars = rawField.match(/[^\w\s\-\.]/g) || [];
        allSpecial.push([...base, `Contains: ${[...new Set(chars)].join(" ")}`, "Info", "No action — characters stripped automatically"]);
      }

      const key = `${normalise(rawTable)}::${normalise(rawField)}`;
      const prev = seen.get(key);
      if (prev) { prev.rows.push(base); }
      else { seen.set(key, { rows: [base] }); }
    }

    const dupes = [...seen.entries()].filter(([, v]) => v.rows.length > 1);
    const dupExtras = dupes.reduce((s, [, v]) => s + v.rows.length - 1, 0);
    for (const [key, { rows }] of dupes) {
      for (const r of rows) {
        allDuplicate.push([...r, `${key.replace("::", ".")} × ${rows.length}`, "Warning", "Review — duplicates get numeric suffix"]);
      }
    }

    const uniqueFields = seen.size;
    // Processable (will be loaded) = total - blank - comment prefix
    const willLoad = allDataRows.length - blankCount - commentCount;
    sheetSummaries.push([
      sheetName, allDataRows.length, blankCount,
      willLoad,                          // ★ will be loaded
      uniqueFields,                      // unique names
      dupExtras, commentCount, specialCount,
    ]);
  }

  const outWb = XLSX.utils.book_new();

  const totalRows       = sheetSummaries.reduce((s, r) => s + (r[1] as number), 0);
  const totalBlanks     = sheetSummaries.reduce((s, r) => s + (r[2] as number), 0);
  const totalProcessable = sheetSummaries.reduce((s, r) => s + (r[3] as number), 0);
  const totalUnique     = sheetSummaries.reduce((s, r) => s + (r[4] as number), 0);
  const totalDupExtras  = sheetSummaries.reduce((s, r) => s + (r[5] as number), 0);
  const totalComments   = sheetSummaries.reduce((s, r) => s + (r[6] as number), 0);
  const totalSpecial    = sheetSummaries.reduce((s, r) => s + (r[7] as number), 0);

  const sumData = [
    ["Combined Validation Report"],
    ["File", file.name],
    ["Sheets analyzed", sheetNames.length],
    [""],
    ["Sheet", "Total Rows", "Blank (dropped)", "Comment (dropped)", "★ Will Be Loaded", "Unique Names", "Duplicate Extras", "Special Chars"],
    ...sheetSummaries.map((r) => [
      r[0], r[1], r[2], r[6], r[3], r[4], r[5], r[7],  // reorder: comment before will-load
    ]),
    [""],
    ["TOTAL", totalRows, totalBlanks, totalComments, totalProcessable, totalUnique, totalDupExtras, totalSpecial],
    [""],
    ["", "", "", "", "★ " + totalProcessable.toLocaleString() + " fields will be loaded across all sheets", totalUnique.toLocaleString() + " unique (duplicates get suffix)", "", ""],
    [""],
    ["Severity Guide"],
    ["Warning", "", "Blank/Invalid, Comment Prefix, and Duplicate rows — review and fix in source if possible"],
    ["Info", "", "Special Characters — stripped automatically, no action needed"],
  ];
  const sumWs = XLSX.utils.aoa_to_sheet(sumData);
  sumWs["!cols"] = [{ wch: 35 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(outWb, sumWs, "Summary");

  const addSheet = (name: string, rows: any[][]) => {
    if (rows.length === 0) {
      XLSX.utils.book_append_sheet(outWb, XLSX.utils.aoa_to_sheet([["No issues found"]]), name);
    } else {
      const ws = XLSX.utils.aoa_to_sheet([reportHeaders, ...rows]);
      ws["!cols"] = [{ wch: 30 }, { wch: 8 }, { wch: 45 }, { wch: 25 }, { wch: 40 }, { wch: 15 }, { wch: 50 }, { wch: 10 }, { wch: 55 }];
      XLSX.utils.book_append_sheet(outWb, ws, name);
    }
  };

  addSheet("Blank Fields", allBlank);
  addSheet("Duplicates", allDuplicate);
  addSheet("Comment Prefix", allComment);
  addSheet("Special Chars", allSpecial);

  const outBuf = XLSX.write(outWb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([outBuf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${file.name.replace(/\.[^.]+$/, "")}_validation_report.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}